import { type ReactNode, useRef } from 'react';
import {
  ChatDrawerBody,
  ChatMarkdown,
  ChatMessage,
  ChatMessageList,
  ChatSurface,
  ChatSurfaceFooter,
  ChatTyping,
  Spinner,
  useFollowBottom,
} from '@wundo/ui';
import type { InsightChatReport } from '@wundo/shared/insightChat.ts';
import { RunChatItem } from './RunChatItem.tsx';
import type { RunChat } from './useRunChat.ts';

export interface RunChatPanelProps {
  chat: RunChat;
  /** The analyst is working: the run is going, or a question is being answered. */
  working: boolean;
  onOpenReport: (report: InsightChatReport) => void;
  /** The composer, standing under the conversation. */
  composer: ReactNode;
}

/**
 * A run's conversation on the same surface as the customer chat: what the
 * analyst did, the questions staff asked, and what it answered.
 *
 * It fills the height it is given: the conversation scrolls inside the surface
 * and the composer stays at its foot. The conversation follows itself down the
 * way the customer chat does — opening at the newest line, and leaving alone a
 * reader who scrolled up.
 */
export function RunChatPanel({
  chat,
  working,
  onOpenReport,
  composer,
}: RunChatPanelProps) {
  const scroller = useRef<HTMLDivElement>(null);
  const streamedLength = chat.streaming.reduce(
    (total, reply) => total + reply.text.length,
    0,
  );
  useFollowBottom(
    scroller,
    `${chat.items.at(-1)?.id}|${chat.items.length}|${streamedLength}|${working}`,
    chat.items.length > 0,
  );

  return (
    <ChatSurface aria-label='Conversation' className='min-h-0 flex-1'>
      <ChatDrawerBody ref={scroller}>
        {chat.isPending && <Spinner />}
        {chat.isError && (
          <p role='alert' className='text-sm text-ink-muted'>
            Failed to load the conversation
          </p>
        )}

        <ChatMessageList>
          {chat.items.map((item) => (
            <RunChatItem key={item.id} item={item} onOpenReport={onOpenReport} />
          ))}
          {chat.streaming.map((reply) => (
            <ChatMessage key={reply.id} author='wundo' authorLabel='Analyst'>
              <ChatMarkdown>{reply.text}</ChatMarkdown>
            </ChatMessage>
          ))}
        </ChatMessageList>

        {working && chat.streaming.length === 0 && (
          <ChatTyping label='The analyst is working' className='mt-3.5' />
        )}
      </ChatDrawerBody>

      <ChatSurfaceFooter>{composer}</ChatSurfaceFooter>
    </ChatSurface>
  );
}
